import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Key,
  User,
  GraduationCap,
  Check,
  ChevronsUpDown,
  CheckCircle,
  XCircle,
  Search
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "";

export default function StudentAccounts() {
  const { getAuthHeader } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, account: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentSearchOpen, setStudentSearchOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/api/admin/student-accounts?limit=500`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/students?limit=500`, { headers: getAuthHeader() })
      ]);
      const accData = accountsRes.data;
      setAccounts(Array.isArray(accData) ? accData : accData.items || []);
      const stuData = studentsRes.data;
      setStudents(Array.isArray(stuData) ? stuData : stuData.items || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setSelectedStudentId("");
    setIsActive(true);
  };

  // Get students that don't have accounts yet
  const availableStudents = students.filter(
    s => !accounts.some(a => a.student_id === s.id)
  );

  const handleCreate = async () => {
    if (!username || !password || !selectedStudentId) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/student-accounts`,
        { username, password, student_id: selectedStudentId },
        { headers: getAuthHeader() }
      );
      toast.success("Student account created");
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to create account:", error);
      toast.error(error.response?.data?.detail || "Failed to create account");
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.account) return;

    const updateData = {};
    if (username && username !== editDialog.account.username) {
      updateData.username = username;
    }
    if (password) {
      updateData.password = password;
    }
    if (isActive !== editDialog.account.is_active) {
      updateData.is_active = isActive;
    }

    if (Object.keys(updateData).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      await axios.put(
        `${API}/api/admin/student-accounts/${editDialog.account.id}`,
        updateData,
        { headers: getAuthHeader() }
      );
      toast.success("Account updated");
      setEditDialog({ open: false, account: null });
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to update account:", error);
      toast.error(error.response?.data?.detail || "Failed to update account");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.account) return;

    try {
      await axios.delete(
        `${API}/api/admin/student-accounts/${deleteDialog.account.id}`,
        { headers: getAuthHeader() }
      );
      toast.success("Account deleted");
      setDeleteDialog({ open: false, account: null });
      fetchData();
    } catch (error) {
      console.error("Failed to delete account:", error);
      toast.error("Failed to delete account");
    }
  };

  const openEditDialog = (account) => {
    setUsername(account.username);
    setPassword("");
    setIsActive(account.is_active);
    setEditDialog({ open: true, account });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Student Accounts</h1>
          <p className="text-slate-500 mt-1">Manage student login credentials</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setCreateDialog(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Account
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <GraduationCap className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Student Portal Login</p>
              <p className="mt-1">Students can access their dashboard at: <code className="bg-blue-100 px-1 rounded">/student-login</code></p>
              <p className="mt-1">They can view their class history, remaining credits, and attendance status.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold">
              {accounts.length} Student Account{accounts.length !== 1 ? "s" : ""}
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-accounts-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Key className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No student accounts created yet</p>
              <p className="text-sm mt-1">Click "Create Account" to add one</p>
            </div>
          ) : (() => {
            const filtered = accounts.filter(a => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              return a.username.toLowerCase().includes(q) || (a.student_name || "").toLowerCase().includes(q);
            });
            return filtered.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No accounts match "{searchQuery}"
              </div>
            ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Username</TableHead>
                  <TableHead className="font-semibold">Student Name</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Created</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((account) => (
                  <TableRow key={account.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {account.username}
                      </div>
                    </TableCell>
                    <TableCell>{account.student_name}</TableCell>
                    <TableCell>
                      {account.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {format(new Date(account.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(account)}
                          data-testid={`edit-account-${account.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog({ open: true, account })}
                          data-testid={`delete-account-${account.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Student Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Student</Label>
              <Popover open={studentSearchOpen} onOpenChange={setStudentSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={studentSearchOpen}
                    className="w-full justify-between"
                    data-testid="select-student-btn"
                  >
                    {selectedStudentId
                      ? students.find(s => s.id === selectedStudentId)?.name
                      : "Select a student..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search students..." />
                    <CommandList>
                      <CommandEmpty>No students found.</CommandEmpty>
                      <CommandGroup>
                        {availableStudents.map((student) => (
                          <CommandItem
                            key={student.id}
                            value={student.name}
                            onSelect={() => {
                              setSelectedStudentId(student.id);
                              setStudentSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedStudentId === student.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {student.name}
                            <span className="ml-2 text-xs text-slate-400">
                              ({student.remaining_classes} classes left)
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="e.g., Guldinez3"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="account-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="account-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700">
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, account: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Student Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Student</Label>
              <Input value={editDialog.account?.student_name || ""} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-username">Username</Label>
              <Input
                id="edit-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={isActive ? "active" : "inactive"} onValueChange={(v) => setIsActive(v === "active")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, account: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-indigo-600 hover:bg-indigo-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, account: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Student Account</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete the account for{" "}
              <strong>{deleteDialog.account?.student_name}</strong>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              The student will no longer be able to log in to the portal.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, account: null })}>
              Cancel
            </Button>
            <Button onClick={handleDelete} variant="destructive">
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
